import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/tokens.css';
import App from './App.js';
import { startWebVitalsReporter } from './lib/web-vitals-reporter.js';
import { capturarSugerenciaWeb } from './lib/sugerencia-web.js';

// #489 · antes de montar nada: si venimos del botón de wb.munya.studio, guardar la página
// sobre la que se quiere comentar. Aquí y no dentro de React, porque sin sesión lo que se
// pinta es el login y el viaje a Microsoft vuelve a «/» sin parámetros.
capturarSugerenciaWeb();

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found');

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// HON-009 PWA · service worker (solo en prod sobre HTTPS · evita ruido dev)
// updateViaCache:'none' fuerza al browser a NO cachear sw.js · clave para detectar
// nuevas versiones del SW tras deploys (evita hard-refresh forzado).
if ('serviceWorker' in navigator && location.protocol === 'https:') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        // Comprueba updates al volver a foco (catch deploys mientras la pestaña estaba abierta)
        const triggerUpdate = (): void => {
          reg.update().catch((e) => {
            console.warn('[munya] sw update failed', e);
          });
        };
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') triggerUpdate();
        });
        // Si llega un SW nuevo en waiting, le pedimos que active y recargamos
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              installing.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
        let reloaded = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (reloaded) return;
          reloaded = true;
          window.location.reload();
        });
      })
      .catch((e) => {
        console.warn('[munya] sw register failed', e);
      });
  });
}

// § 15.bis · RUM Web Vitals reporter (sólo en https://)
startWebVitalsReporter();

// pwa_offline_full · si flag ON, instruye al SW para pre-cachear toda la API GET.
// El flag se consulta una vez al boot · cambios requieren full reload del SW.
if ('serviceWorker' in navigator) {
  fetch('/api/v1/flags', { credentials: 'same-origin' })
    .then((r) => (r.ok ? (r.json() as Promise<{ flags?: Record<string, boolean> }>) : null))
    .then((d) => {
      const enabled = d?.flags?.pwa_offline_full === true;
      void navigator.serviceWorker.ready.then((reg) => {
        reg.active?.postMessage({ type: 'SET_OFFLINE_FULL', enabled });
      });
    })
    .catch(() => {
      /* sin flags · SW usa default network-first */
    });
}

// K2 § 7.11-ter · Background sync mutations
// Cuando vuelve la conexión, reproduce la cola IDB de mutaciones offline.
// Registra Background Sync API del SW como backup (Chromium · best-effort).
window.addEventListener('online', () => {
  void import('./lib/idb-queue.js').then(({ replayQueue, pendingCount }) =>
    pendingCount().then((n) => {
      if (n > 0) {
        void replayQueue();
      }
    }),
  );
});
if ('serviceWorker' in navigator && 'SyncManager' in window) {
  navigator.serviceWorker.ready
    .then((reg) =>
      (
        reg as ServiceWorkerRegistration & { sync?: { register: (tag: string) => Promise<void> } }
      ).sync?.register('munya-replay'),
    )
    .catch(() => {
      // Background Sync no soportado · fallback online-event basta
    });
  navigator.serviceWorker.addEventListener('message', (ev: MessageEvent) => {
    const d = ev.data as { type?: string } | null;
    if (d?.type === 'REPLAY_QUEUE') {
      void import('./lib/idb-queue.js').then(({ replayQueue }) => replayQueue());
    }
  });
}
